// Two-step sign-in's key and codes (27-admin.md, "Two-step sign-in"; RFC 4648 base32, RFC 6238
// TOTP with SHA-1, 6 digits, 30 s): a new 160-bit key, its authenticator address, and the check that
// a typed code fits the key now or half a minute before or after.
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

export function base32(bytes: Uint8Array): string {
  let bits = 0;
  let value = 0;
  let out = '';
  for (const b of bytes) {
    value = (value << 8) | b;
    bits += 8;
    for (; bits >= 5; bits -= 5) out += ALPHABET[(value >>> (bits - 5)) & 31];
  }
  return bits ? out + ALPHABET[(value << (5 - bits)) & 31] : out;
}

function unbase32(text: string): Uint8Array {
  const bytes: number[] = [];
  let bits = 0;
  let value = 0;
  for (const c of text.toUpperCase().replace(/[^A-Z2-7]/g, '')) {
    value = (value << 5) | ALPHABET.indexOf(c);
    bits += 5;
    if (bits >= 8) bytes.push((value >>> (bits -= 8)) & 255);
  }
  return new Uint8Array(bytes);
}

export const newKey = () => base32(crypto.getRandomValues(new Uint8Array(20)));

export const otpAddress = (key: string, issuer: string, account: string) =>
  `otpauth://totp/${encodeURIComponent(`${issuer}:${account}`)}?secret=${key}&issuer=${encodeURIComponent(issuer)}&algorithm=SHA1&digits=6&period=30`;

/** The code for a 30-second step. */
export async function totp(key: string, step: number): Promise<string> {
  const counter = new Uint8Array(8);
  new DataView(counter.buffer).setBigUint64(0, BigInt(step));
  const hmacKey = await crypto.subtle.importKey('raw', unbase32(key) as BufferSource, { name: 'HMAC', hash: 'SHA-1' }, false, ['sign']);
  const mac = new Uint8Array(await crypto.subtle.sign('HMAC', hmacKey, counter));
  const at = mac[19] & 15;
  const n = ((mac[at] & 127) << 24) | (mac[at + 1] << 16) | (mac[at + 2] << 8) | mac[at + 3];
  return String(n % 1_000_000).padStart(6, '0');
}

/** A typed code fits now, or half a minute before or after (spaces ignored). */
export async function fits(key: string, typed: string, now = Date.now()): Promise<boolean> {
  const code = typed.replace(/\s/g, '');
  const step = Math.floor(now / 30_000);
  for (const s of [step - 1, step, step + 1]) if ((await totp(key, s)) === code) return true;
  return false;
}
